import { useRef } from 'react';
import { Loader2, X, Upload, Info } from 'lucide-react';
import { IndianRupee } from './CustomIcons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { toast } from 'react-toastify';

const CreateWebinarPopup = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  onInputChange,
  isSubmitting,
  uploadingImage,
  fileInputRef,
  onImageUpload,
  imagePreview,
  setImagePreview,
  isEdit
}) => {
  const popupRef = useRef(null);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'check',
    'indent', 'align', 'link', 'image', 'video'
  ];

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      onClose();
    }
  };

  const handleCancel = () => {
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  const handleRemoveImage = () => {
    onInputChange({ target: { name: 'mentorimg', value: null } });
    if (setImagePreview) {
      setImagePreview('');
    }
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleSharedInfoChange = (content) => {
    onInputChange({ target: { name: 'sharedInfo', value: content } });
  };

  const handleDescriptionChange = (content) => {
    onInputChange({ target: { name: 'webinardescription', value: content } });
  };

  return (
    <div
      onClick={handleOverlayClick}
      className="fixed inset-0 backdrop-blur-md bg-gray-900/60 flex items-center justify-center z-[400] p-4"
    >
      <div
        ref={popupRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-4xl w-full mx-4 flex flex-col max-h-[90vh]"
      >
        {/* Fixed Header */}
        <div className="flex justify-between items-center px-8 pt-8 pb-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">{isEdit ? 'Edit Webinar' : 'Create New Webinar'}</h3>
          <button
            onClick={handleCancel}
            className="text-gray-500 text-2xl hover:text-gray-700"
          >
            &times;
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 px-8 py-4">
          <form onSubmit={onSubmit} id="webinar-form">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="webinarname">
                Webinar Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="webinarname"
                name="webinarname"
                value={formData.webinarname}
                onChange={onInputChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                placeholder="Enter webinar title"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="bg-white rounded-lg overflow-hidden border border-gray-300 focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500 transition-all">
                <ReactQuill
                  theme="snow"
                  value={formData.webinardescription || ''}
                  onChange={handleDescriptionChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter webinar description..."
                  className="webinar-description-editor"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="mentorname">
                Mentor Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="mentorname"
                name="mentorname"
                value={formData.mentorname}
                onChange={onInputChange}
                required
                className="w-full px-3 py-2 border text-sm border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                placeholder="Enter mentor name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Mentor Profile Image
              </label>
              <div className="flex items-center gap-4">
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Profile preview"
                      className="w-16 h-16 rounded-full object-cover border-2 border-amber-500"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/64?text=No+Image';
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={onImageUpload}
                    accept="image/jpeg,image/png,image/jpg,image/gif"
                    className="hidden"
                    id="profile-image-upload"
                  />
                  <label
                    htmlFor="profile-image-upload"
                    className="flex items-center justify-center text-sm gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer w-full hover:bg-gray-50 transition-colors"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        {imagePreview ? 'Change Image' : 'Upload Image'}
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: JPEG, PNG, JPG, GIF (Max 5MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Price Input Field */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="price">
                <div className="flex items-center gap-1">
                  <IndianRupee className="w-4 h-4" />
                  <span>Price (in INR)</span>
                </div>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <IndianRupee className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={onInputChange}
                  step="0.01"
                  min="0"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  placeholder="0.00"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Leave empty or enter 0 for free webinars
              </p>
            </div>

            {/* Shared Info Field with React Quill */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                <div className="flex items-center gap-1">
                  <Info className="w-4 h-4" />
                  <span>Shared Information</span>
                </div>
              </label>
              <div className="bg-white rounded-lg overflow-hidden border border-gray-300 focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500 transition-all">
                <ReactQuill
                  theme="snow"
                  value={formData.sharedInfo || ''}
                  onChange={handleSharedInfoChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter any additional information to share (meeting links, resources, materials, etc.)"
                  className="webinar-shared-info-editor"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                This information will be shared with webinar participants. You can format text, add links, images, and videos.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="asondate">
                Webinar Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                id="asondate"
                name="asondate"
                value={formData.asondate}
                onChange={onInputChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              />
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div className="flex gap-3 px-8 py-6 border-t border-gray-200 flex-shrink-0 bg-white rounded-b-2xl">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-black rounded-lg text-white font-medium hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="webinar-form"
            disabled={isSubmitting || uploadingImage}
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium text-sm py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEdit ? 'Update Webinar' : 'Create Webinar'
            )}
          </button>
        </div>
      </div>

      <style>{`
        /* Quill Editor Styles */
        .ql-container {
          font-size: 14px;
          border-radius: 0 0 8px 8px;
        }

        .webinar-description-editor .ql-container {
          height: 380px !important;
        }

        .webinar-shared-info-editor .ql-container {
          height: 280px !important;
        }
        
        .ql-toolbar {
          border-radius: 8px 8px 0 0;
          background-color: #fafafa;
          border-bottom: 1px solid #e5e7eb;
          border-top: none;
          border-left: none;
          border-right: none;
        }
        
        .ql-editor {
          height: 100% !important;
          overflow-y: auto !important;
          font-family: inherit;
        }
        
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        
        /* Make sure toolbar buttons are clickable */
        .ql-toolbar button {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        .ql-toolbar .ql-picker {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        .ql-toolbar .ql-picker-label {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        /* Ensure proper z-index for dropdowns */
        .ql-picker-options {
          z-index: 1000 !important;
        }
        
        /* Fix for button styles */
        .ql-toolbar button:hover {
          background-color: #e5e7eb !important;
        }
        
        .ql-toolbar button.ql-active {
          background-color: #e5e7eb !important;
          color: #f59e0b !important;
        }
        
        .ql-snow .ql-picker.ql-expanded .ql-picker-options {
          border-color: #e5e7eb;
        }
        
        /* Consistent height for all form elements */
        input,
        textarea,
        .ql-container,
        .ql-editor,
        button {
          transition: all 0.2s ease;
        }
        
        /* Make the Quill editor container consistent */
        .ql-container.ql-snow {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }
        
        /* Ensure proper spacing */
        .ql-editor {
          padding: 12px;
        }
        
        /* Custom scrollbar for the body */
        .overflow-y-auto::-webkit-scrollbar {
          width: 8px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default CreateWebinarPopup;